import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'redux-first-router-link'
import EditorButton from 'components/EditorButton'

import ScrollerContext from 'components/Scroller/Context'

import { doUpdateEditorVisibility } from 'ducks/editor/operations'
import { selectOpenNotePageAction } from 'selectors/pageActions'
import { propFunc } from 'util/functions'

import styles from './styles.less'
import AgentCollision from '../AgentCollision'
import WithNoteEditCallout from './WithNoteEditCallout'

const Selector = ({ onClick, ticketId }) => {
  const openNote = useSelector(selectOpenNotePageAction)
  const [isAnimationEnded, setIsAnmationEnded] = useState(true)
  const { getScrollerAPI } = useContext(ScrollerContext)
  const containerRef = useRef(null)
  const iObserverRef = useRef(null)
  const detectorRef = useRef(null)
  const dispatch = useDispatch()

  const handleClick = useCallback(
    () => {
      dispatch(doUpdateEditorVisibility(true))
      if (onClick) {
        onClick()
      }
    },
    [onClick, dispatch]
  )

  const handleIntersection = useCallback(
    entry => {
      const container = containerRef.current
      if (!container) return
      // NOTE (jscheel): We don't use state here because react can async and
      // batch update the state, and also updating state requires a re-render.
      // We need this class update to happen *as fast as possible*.
      if (entry.intersectionRatio > 0) {
        container.classList.toggle('floating', false)
      } else {
        container.classList.toggle('floating', true)
      }
    },
    [containerRef]
  )

  useEffect(
    () => {
      const container = getScrollerAPI().getElement()
      let iObserver = iObserverRef.current
      if (container && !iObserver && window.IntersectionObserver) {
        iObserver = new window.IntersectionObserver(
          entries => {
            entries.forEach(handleIntersection)
          },
          {
            root: container,
            threshold: [0, 1],
            rootMargin: '0px 0px 1px 0px',
          }
        )
        iObserverRef.current = iObserver
      }

      return () => {
        if (iObserver) {
          iObserver.disconnect()
        }
      }
    },
    [iObserverRef, getScrollerAPI, handleIntersection]
  )

  useEffect(
    () => {
      const iObserver = iObserverRef.current
      if (!iObserver) return
      const detector = detectorRef.current
      if (detector) iObserver.observe(detector)
    },
    [iObserverRef, detectorRef]
  )

  return (
    <React.Fragment>
      <div
        ref={containerRef}
        className={styles.Selector}
        data-test-id="composer-selector"
        onAnimationEnd={propFunc(setIsAnmationEnded, true)}
        onAnimationStart={propFunc(setIsAnmationEnded, false)}
      >
        <EditorButton
          type="info"
          data-test-id="composer-selector-reply-add"
          className={styles.SelectorButton}
          onClick={handleClick}
          size="small"
        >
          Reply <span className={styles.HotKeyIndicator}>[r]</span>
        </EditorButton>
        <Link
          to={openNote}
          onClick={handleClick}
          data-test-id="composer-selector-comment-add"
        >
          <WithNoteEditCallout
            isTriggerContainerAnimationEnded={isAnimationEnded}
          >
            <EditorButton
              type="noteOutlineFilledHover"
              editorButtonType="note"
              className={styles.SelectorNoteButton}
              size="small"
            >
              Post a note <span className={styles.HotKeyIndicator}>[n]</span>
            </EditorButton>
          </WithNoteEditCallout>
        </Link>
        <AgentCollision conversationId={ticketId} className="grui ml-auto" />
      </div>
      <div className={styles.SelectorBottomDetector} ref={detectorRef} />
    </React.Fragment>
  )
}

export default Selector
